import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../store/reducers';
import { FileText, Sparkles, FileCheck, Download, ArrowRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { statsCards, volumeMensuel, parJuridiction, decisions, statutLabels } from '../data/mockData';
import './Dashboard.css';

const iconMap: Record<string, React.ReactNode> = {
  'file-text': <FileText size={20} />,
  'sparkles': <Sparkles size={20} />,
  'file-check': <FileCheck size={20} />,
  'download': <Download size={20} />,
};

const Dashboard: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  const displayName = user?.username || 'Agent';

  return (
    <div className="dashboard">
      {/* Welcome section */}
      <div className="dashboard-welcome">
        <div className="dashboard-welcome-text">
          <h2>Bonjour, {displayName}</h2>
          <p>Bienvenue dans votre espace de travail. Voici un résumé de l'activité.</p>
        </div>
        <div className="dashboard-welcome-actions">
          <button className="btn-primary" onClick={() => navigate('/espace-cndj/decisions')}>
            Voir les décisions <ArrowRight size={16} />
          </button>
          <button className="btn-secondary" onClick={() => navigate('/espace-cndj/numerisation')}>
            Importer
          </button>
        </div>
      </div>

      {/* Stats cards */}
      <div className="dashboard-stats">
        {statsCards.map((card, index) => (
          <div key={index} className="stat-card">
            <div className="stat-card-icon">{iconMap[card.icon]}</div>
            <div className="stat-card-info">
              <span className="stat-card-label">{card.label}</span>
              <span className="stat-card-value">{card.value}</span>
              {card.change && <span className="stat-card-change">{card.change}</span>}
              {card.sublabel && <span className="stat-card-sublabel">{card.sublabel}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="dashboard-charts">
        <div className="chart-card">
          <h3 className="chart-title">Volume mensuel des décisions</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={volumeMensuel}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="mois" axisLine={false} tickLine={false} fontSize={12} />
              <YAxis axisLine={false} tickLine={false} fontSize={12} />
              <Tooltip />
              <Bar dataKey="valeur" fill="#E85D04" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3 className="chart-title">Répartition par juridiction</h3>
          <div className="donut-container">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={parJuridiction}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {parJuridiction.map((entry, index) => (
                    <Cell key={index} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="donut-legend">
              {parJuridiction.map((item, index) => (
                <div key={index} className="donut-legend-item">
                  <span className="donut-legend-dot" style={{ background: item.color }} />
                  <span className="donut-legend-label">{item.name}</span>
                  <span className="donut-legend-value">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* File de traitement */}
      <div className="dashboard-queue">
        <div className="queue-header">
          <h3 className="chart-title">File de traitement</h3>
          <button className="btn-link" onClick={() => navigate('/espace-cndj/decisions')}>
            Voir tout <ArrowRight size={14} />
          </button>
        </div>
        <table className="queue-table">
          <thead>
            <tr>
              <th>N° Décision</th>
              <th>Juridiction</th>
              <th>Nature</th>
              <th>Date</th>
              <th>Statut</th>
              <th>Agent</th>
            </tr>
          </thead>
          <tbody>
            {decisions.slice(0, 5).map((d) => {
              const s = statutLabels[d.statut];
              return (
                <tr key={d.id} onClick={() => navigate(`/espace-cndj/decisions/${d.id}`)} className="queue-row-clickable">
                  <td className="queue-numero">{d.numero}</td>
                  <td>{d.juridiction}</td>
                  <td>{d.nature}</td>
                  <td>{new Date(d.date).toLocaleDateString('fr-FR')}</td>
                  <td>
                    <span className="status-badge" style={{ color: s.color, background: s.bg }}>
                      {s.label}
                    </span>
                  </td>
                  <td>{d.agent}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Dashboard;
